import { FC, MouseEvent, ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { REDESIGN_NS } from "../i18n";
import { IconClose } from "./icons";

interface ModalProps {
  isOpen: boolean;
  title: ReactNode;
  onClose: () => void;
  width: number;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Ortak modal kabuğu. components/Dialog genel bir modal değil, react-confirm
 * tabanlı sabit metinli bir onay kutusu; form modalları için kullanılamıyor.
 */
export const Modal: FC<ModalProps> = ({
  isOpen,
  title,
  onClose,
  width,
  children,
  footer,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const stop = (event: MouseEvent) => event.stopPropagation();

  return (
    <div className="sso-rd__overlay" onClick={onClose} role="presentation">
      <div
        className="sso-rd__modal"
        style={{ maxWidth: width }}
        onClick={stop}
        role="dialog"
        aria-modal="true"
      >
        <div className="sso-rd__modal-head">
          <div className="sso-rd__modal-title">{title}</div>
          <ModalCloseButton onClose={onClose} />
        </div>
        {children}
        {footer}
      </div>
    </div>
  );
};

const ModalCloseButton: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation(REDESIGN_NS);
  return (
    <button
      type="button"
      className="sso-rd__modal-close"
      onClick={onClose}
      title={t("Close")}
      aria-label={t("Close")}
    >
      <IconClose size={18} />
    </button>
  );
};

interface FooterProps {
  onClose: () => void;
  onSubmit: () => void;
  submitLabel: string;
  isBusy?: boolean;
}

export const ModalFooter: FC<FooterProps> = ({
  onClose,
  onSubmit,
  submitLabel,
  isBusy,
}) => {
  const { t } = useTranslation(REDESIGN_NS);
  return (
    <div className="sso-rd__modal-foot">
      <button
        type="button"
        className="sso-rd__btn sso-rd__btn--modal sso-rd__btn--secondary"
        onClick={onClose}
      >
        {t("Close")}
      </button>
      <button
        type="button"
        className={
          "sso-rd__btn sso-rd__btn--modal sso-rd__btn--primary" +
          (isBusy ? " is-busy" : "")
        }
        onClick={onSubmit}
        disabled={isBusy}
        aria-busy={isBusy}
      >
        {isBusy && <span className="sso-rd__spinner" aria-hidden="true" />}
        {submitLabel}
      </button>
    </div>
  );
};

interface ConfirmProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isBusy?: boolean;
  /** Verilmezse genel onay metni gösterilir. Geri alınamayan işlemler kendi metnini verir. */
  message?: ReactNode;
}

/** Silme onayı; z-index'i diğer modalların üstündedir. */
export const ConfirmModal: FC<ConfirmProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isBusy,
  message,
}) => {
  const { t } = useTranslation(REDESIGN_NS);

  if (!isOpen) return null;

  return (
    <div
      className="sso-rd__overlay sso-rd__overlay--confirm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="sso-rd__modal sso-rd__modal--confirm"
        onClick={(event: MouseEvent) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="sso-rd__confirm">
          <div className="sso-rd__confirm-text">
            {message ?? t("Do you want to continue with your action?")}
          </div>
          <div className="sso-rd__confirm-actions">
            <button
              type="button"
              className="sso-rd__btn sso-rd__btn--modal sso-rd__btn--secondary"
              onClick={onClose}
            >
              {t("Close")}
            </button>
            <button
              type="button"
              className={
                "sso-rd__btn sso-rd__btn--modal sso-rd__btn--primary" +
                (isBusy ? " is-busy" : "")
              }
              onClick={onConfirm}
              disabled={isBusy}
              aria-busy={isBusy}
            >
              {isBusy && <span className="sso-rd__spinner" aria-hidden="true" />}
              {t("Confirm")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
